import {
    Alert,
    Button,
    Flashbar,
    FlashbarProps,
    SpaceBetween,
    TextContent
} from "@amzn/awsui-components-react";
import {
    Container,
    ContainerRef,
    EntityType,
    FreshnessState,
    ILegalContact,
    LegalContact,
    PageFactory,
    WhosMyLawyerContent
} from "@amzn/ask-legal-domain";
import * as React from "react";
import { AppContext } from "../../setup/context";
import { useAPI2 } from "../../hooks/api-hook";
import { useServerSideCollection } from "../../hooks/legal-contacts-collection-hook";
import { LegalContactsPolarisFactory } from "../../factory/polaris/legal-contacts-polaris-factory";
import { LegalContactModel } from "../../model/legal-contact-model";
import { ServerSideLegalContactsTable } from "./ServerSideLegalContactsTable";
import { DeleteLegalContact } from "./modal-content/DeleteLegalContact";
import { UpdateLegalContactModal } from "./modal-content/UpdateLegalContact";
import { getQueryParameterMap, openInNewTab, setURLByQueryParameters } from "../../utils/common-utils";

export const MyLegalContactsTable = (props: {
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);
    // Loads the viewer's OWN contacts plus their direct and skip-level reports' contacts.
    const loadAllLegalContactsByManagerApiRunner = useAPI2(
        context.getLegalContactAPI().loadAllByManager
    );
    const baselineContactApiRunner = useAPI2(
        context.getLegalContactAPI().update
    );
    const [flashbarItems, setFlashBarItems] = React.useState<Array<FlashbarProps.MessageDefinition>>([]);

    // Escalation emails deep-link here with ?alias=<login>; seed the alias filter so the manager
    // lands pre-filtered to that report. The filter chip is clearable to reveal the whole team.
    // Read once on mount so a later re-render doesn't re-seed after the user clears the chip.
    const initialFilterTokens = React.useMemo(() => {
        const aliasParam = getQueryParameterMap().get("alias");
        return aliasParam
            ? [{ propertyKey: LegalContactsPolarisFactory.Filters.PropertyFilters.LegalContactUserAlias.key, operator: "=", value: aliasParam }]
            : undefined;
    }, []);

    // Strip ?alias= from the URL after seeding, so clearing the chip and refreshing (or sharing
    // the URL) doesn't silently re-apply the filter. It's a one-shot pre-filter, not persistent state.
    React.useEffect(() => {
        const params = getQueryParameterMap();
        if (params.has("alias")) {
            params.delete("alias");
            setURLByQueryParameters(params);
        }
    }, []);

    const collection = useServerSideCollection(
        loadAllLegalContactsByManagerApiRunner,
        props.instanceId,
        {
            columnDefinitions: LegalContactsPolarisFactory.Table.toMyContactsColumnDefinition(),
            // Default to freshness order so the most overdue contacts surface first.
            defaultSortingColumn: { sortingField: "freshnessDate" },
            filteringProperties: LegalContactsPolarisFactory.Filters.PropertyFilters.toMyContactsFilteringProperties(),
            selectionType: "single",
            initialFilterTokens,
            filteringOptions: LegalContactsPolarisFactory.Filters.PropertyFilters.ManagerRelationshipOptions
        }
    );

    const onBaselineUpdate = (updatedRecord: LegalContact.Record) => {
        // Resolve the row index inside the updater against the latest state — a concurrent
        // re-fetch or sort could have reordered items between render and this update.
        collection.tableProps.setProps((prev) => {
            const idx = prev.items.findIndex(x =>
                x.ref.entityRef.entityId === updatedRecord.ref.entityRef.entityId
            );
            if (idx < 0) return prev;
            // Preserve the row's existing relationship (self/direct/skip); the update response
            // doesn't carry it. Only freshness changes on baseline.
            const existing = prev.items[idx];
            const merged: ILegalContact.LoadAllLegalContactsByManagerOutput = {
                ...existing,
                ...updatedRecord,
                freshnessStatus: FreshnessState.FRESH,
                managerRelationship: existing.managerRelationship
            };
            return {
                ...prev,
                items: [...prev.items.slice(0, idx), merged, ...prev.items.slice(idx + 1)]
            };
        });
    };

    const baselineLegalContact = () => {
        if (collection.tableProps.props.selectedItems.length < 1) return;
        baselineContactApiRunner.invoke({
            containerId: collection.tableProps.props.selectedItems[0].containerId,
            entityVersionRef: collection.tableProps.props.selectedItems[0].ref,
            baselineFreshness: true
        });
    };

    // Row selected for deletion. The admin/my-contacts table is the ONLY surface that renders a
    // contact orphaned on a deprecated page (its live page no longer displays it), so deleting
    // from here is the only way to remove such a record (ticket P524982611). The DeleteLegalContact
    // modal needs only entityVersionRef, containerId and the display name — all present on the row —
    // so we adapt the row to the modal's UpdateState-shaped `state` prop rather than rebuilding the
    // container-bound hook per row.
    const [contactToDelete, setContactToDelete] =
        React.useState<ILegalContact.LoadAllLegalContactsByManagerOutput | undefined>(undefined);

    const requestDelete = () => {
        if (collection.tableProps.props.selectedItems.length < 1) return;
        setContactToDelete(collection.tableProps.props.selectedItems[0]);
    };

    const onDeleted = (deleted: LegalContact.Record) => {
        // Optimistically splice the just-deleted row out of the local items AND clear the
        // selection in the same update, BEFORE the async re-fetch below completes. Without this,
        // the deleted row stays visible and selected during the re-fetch window, so a second
        // Delete click fires a remove() on an already-deleted entity -> 404 "Legal contact not
        // found" (jamden hit this by clicking Delete three times on one record, P531xxxxx). The
        // server-side counts are reconciled by loadItems() immediately after; this is purely to
        // close the double-submit window.
        collection.tableProps.setProps((prev) => ({
            ...prev,
            items: prev.items.filter(x =>
                x.ref.entityRef.entityId !== deleted.ref.entityRef.entityId
            ),
            selectedItems: []
        }));
        // Re-fetch from the server to reconcile totalItemsCount/pagesCount: this is a server-side
        // collection, so those counts are tracked independently of the local items array and a
        // local splice alone would leave pagination stale (off by one, or a now-empty page).
        collection.loadItems();
        setFlashBarItems([{
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setFlashBarItems([]),
            header: "Legal contact deleted",
            content: `${deleted.legalContactUser.name} has been removed.`
        }]);
    };

    // Adapt the selected row to the shape DeleteLegalContact reads (state.entityVersionRef.value,
    // state.containerRef.id, state.legalContactUser.value.name). Only these three are consumed.
    const deleteModalState = contactToDelete && ({
        legalContactUser: { value: contactToDelete.legalContactUser },
        containerRef: { id: contactToDelete.containerId },
        entityVersionRef: { value: contactToDelete.ref }
    } as any as LegalContactModel.UpdateState);

    // Row selected for in-place edit. The UpdateState hook is lifted here (not into a per-row child)
    // and seeded synchronously via init() in the edit handler BEFORE the modal opens, mirroring the
    // live page (WhosMyLawyerContentView): UpdateLegalContactModal reads legalContactUser.value.name
    // on its first render, so the state must already be populated when it mounts.
    const [editModalOpen, setEditModalOpen] = React.useState(false);

    // Single-selection table: the currently selected row, if any.
    const selected = collection.tableProps.props.selectedItems[0] as
        ILegalContact.LoadAllLegalContactsByManagerOutput | undefined;
    // View and Edit both open/load the contact's page container, which requires CAN_VIEW on it.
    // The row carries canViewContainer (computed server-side) so we show-but-DISABLE both with a
    // reason when the caller lacks page access. Delete does not require page access (owner/manager
    // authorized server-side), so it stays enabled — this is the P529127998 self-service split.
    const canActOnPage = !!selected?.canViewContainer;
    const noPageAccessReason =
        "You don't have access to the page this legal contact is on, so you can't view or edit it here. You can still delete it.";

    // VIEW: deep-link to the contact's live page in a new tab. Dead-ends without page access, hence
    // the canViewContainer gate on the button. No editId param: that param makes the live page auto-
    // open the update modal (the old 'Update Contact' behavior), which contradicts a view-only intent.
    // Editing has its own in-place button (requestEdit); this matches the plain ?container= view links
    // used elsewhere (ContainerLink, favorites, AllLegalContactsTable).
    const viewOnPage = () => {
        if (!selected) return;
        const pageId = PageFactory.fromEntityId(selected.containerId);
        const url = `#/page/${pageId}/live?container=${selected.containerId}`;
        openInNewTab(url);
    };

    // Edit form state, bound to the selected row's container, memoized by container id. The hook
    // requires a containerRef at call time (falls back to "" before any selection); memoizing keeps
    // its identity stable, because EditLegalContactForm's load effect depends on it by object
    // identity — a fresh literal each render would re-fire the loader endlessly (spinner → flicker).
    // Must be a full ContainerRef, not a bare EntityRef: the /container/load-version API Gateway model
    // requires containerType (P524982611). This tab is always WML, so the type is fixed.
    const editContainerRef: ContainerRef = React.useMemo(
        () => ({
            id: selected?.containerId ?? "",
            type: EntityType.Container,
            containerType: Container.Type.WHOS_MY_LAWYER
        }),
        [selected?.containerId]
    );
    const editState = LegalContactModel.UpdateState.use({
        containerRef: editContainerRef
    });

    const requestEdit = () => {
        if (!selected || !selected.canViewContainer) return;
        // init() before open — see editModalOpen above. The row extends LegalContact.Record, so
        // it's a valid init() argument; EditLegalContactForm re-loads the authoritative record after
        // and derives the container's options/additional-fields config from its own load.
        editState.init(selected);
        setEditModalOpen(true);
    };

    const dismissEdit = () => {
        editState.reset();
        setEditModalOpen(false);
    };

    const onEdited = (updated: LegalContact.Record) => {
        // Server-side collection: re-fetch so freshness/relationship and counts stay authoritative
        // (same reasoning as onDeleted). Then clear selection so the action buttons don't stay
        // enabled against a stale row.
        collection.loadItems();
        collection.tableProps.setProps((prev) => ({ ...prev, selectedItems: [] }));
        setFlashBarItems([{
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setFlashBarItems([]),
            header: "Legal contact updated",
            content: `${updated.legalContactUser.name} has been updated.`
        }]);
    };

    const headerActions = <SpaceBetween size="s" direction="horizontal">
        <Button
            iconName="external"
            disabled={!selected || !canActOnPage}
            disabledReason={selected && !canActOnPage ? noPageAccessReason : undefined}
            onClick={viewOnPage}
        >
            View Contact
        </Button>
        <Button
            iconName="edit"
            disabled={!selected || !canActOnPage}
            disabledReason={selected && !canActOnPage ? noPageAccessReason : undefined}
            onClick={requestEdit}
        >
            Edit Contact
        </Button>
        <Button
            iconName="remove"
            disabled={!selected}
            onClick={requestDelete}
        >
            Delete Contact
        </Button>
        <Button
            disabled={!selected}
            loading={baselineContactApiRunner.status === "Running"}
            onClick={baselineLegalContact}
            variant="primary"
        >
            Baseline Contact
        </Button>
    </SpaceBetween>;

    const baselineInfoAlert = <Alert type="info" header="How to baseline your information">
        <TextContent>
            <ol>
                <li>Select the contact you want to baseline</li>
                <li>Click on the <b>baseline</b> / <b>update</b> action to baseline</li>
            </ol>
        </TextContent>
    </Alert>;

    React.useEffect(() => {
        if (baselineContactApiRunner.status === "Running") {
            setFlashBarItems([{
                type: "info",
                loading: true,
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setFlashBarItems([]),
                header: "Baseline contact in progress",
                content: "Please wait..."
            }]);
        } else if (baselineContactApiRunner.status === "Succeeded") {
            setFlashBarItems([{
                type: "success",
                loading: false,
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setFlashBarItems([]),
                header: "Baseline contact completed",
                content: "Your information has been saved"
            }]);
            onBaselineUpdate(baselineContactApiRunner.output);
        } else if (baselineContactApiRunner.status === "Error") {
            setFlashBarItems([{
                type: "error",
                loading: false,
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setFlashBarItems([]),
                header: "Baseline contact failed",
                content: baselineContactApiRunner.err.message
            }]);
        }
    }, [baselineContactApiRunner.status]);

    return <SpaceBetween size="s">
        <Flashbar
            items={flashbarItems}
            i18nStrings={{
                ariaLabel: "Notifications",
                notificationBarAriaLabel: "View all notifications",
                notificationBarText: "Notifications",
                errorIconAriaLabel: "Error",
                warningIconAriaLabel: "Warning",
                successIconAriaLabel: "Success",
                infoIconAriaLabel: "Info",
                inProgressIconAriaLabel: "In progress"
            }}
        />
        {baselineInfoAlert}
        <ServerSideLegalContactsTable collection={collection} headerActions={headerActions} />
        {deleteModalState &&
            <DeleteLegalContact
                state={deleteModalState}
                onDismiss={() => setContactToDelete(undefined)}
                onDelete={(deleted) => {
                    onDeleted(deleted);
                    setContactToDelete(undefined);
                }}
            />
        }
        {editModalOpen && selected &&
            <UpdateLegalContactModal
                state={editState}
                // EditLegalContactForm self-loads the container version and derives scope/team/
                // sub-team options AND additional-fields config from it, so this tab need not supply
                // real content — an empty literal is enough (AutoSDE r2p3 removed the second load).
                content={{ dataOptions: {} } as unknown as WhosMyLawyerContent}
                onUpdate={(updated) => {
                    onEdited(updated);
                    dismissEdit();
                }}
                onDismiss={dismissEdit}
            />
        }
    </SpaceBetween>;
};